import { useContext, useEffect, useReducer, useRef } from "react";
import { useIntl } from "react-intl";
import { WmButton } from "@watermarkinsights/ripple-react";
import { AppContext, AppContextInterface } from "@/contexts/AppContext";
import {
  generateDraftMessage,
  type DraftTone,
  type DraftLength,
  type DraftMessageOutput,
} from "./draftMessageService";
import { PillGroup, TONE_OPTIONS, LENGTH_OPTIONS } from "./DraftPillGroup";
import DraftCardPreview from "./DraftCardPreview";
import DraftResponseView, { DraftAdjustFooter } from "./DraftResponseView";
import { QuotaLimitBanner } from "../QuotaLimitBanner";
import "./AiDraftMessage.scss";

export interface MessagePair {
  id: string;
  question: string;
  response: string;
}

interface DraftConversationPanelProps {
  messagePairs: MessagePair[];
  onBackToConversation: () => void;
  onResultStateChange?: (hasResult: boolean) => void;
  resetSignal?: number;
}

// ── Reducer ────────────────────────────────────────────────────────────────────

type Action =
  | { type: "setTone"; tone: DraftTone }
  | { type: "setLength"; length: DraftLength }
  | { type: "toggleId"; id: string }
  | { type: "selectAll"; allIds: string[] }
  | { type: "toggleExpand"; id: string }
  | { type: "generateWithoutSelection" }
  | { type: "generateStart" }
  | { type: "generateSuccess"; output: DraftMessageOutput }
  | { type: "generateError"; error: string }
  | { type: "adjustStart" }
  | { type: "adjustSuccess"; output: DraftMessageOutput }
  | { type: "adjustError"; error: string }
  | { type: "resetToSelection" };

interface State {
  tone: DraftTone;
  length: DraftLength;
  selectedIds: string[];
  isGenerating: boolean;
  error: string | null;
  draftOutput: DraftMessageOutput | null;
  expandedId: string | null;
  isAdjusting: boolean;
  adjustError: string | null;
  hasAttemptedWithoutSelection: boolean;
  selectionAttemptCount: number;
}

function createInitialState(messagePairs: MessagePair[]): State {
  return {
    tone: "professional",
    length: "standard",
    selectedIds: messagePairs.map((p) => p.id),
    isGenerating: false,
    error: null,
    draftOutput: null,
    expandedId: null,
    isAdjusting: false,
    adjustError: null,
    hasAttemptedWithoutSelection: false,
    selectionAttemptCount: 0,
  };
}

const reducer = (state: State, action: Action): State => {
  switch (action.type) {
    case "setTone":
      return { ...state, tone: action.tone };
    case "setLength":
      return { ...state, length: action.length };
    case "toggleId": {
      const isSelected = state.selectedIds.includes(action.id);
      return {
        ...state,
        selectedIds: isSelected
          ? state.selectedIds.filter((id) => id !== action.id)
          : [...state.selectedIds, action.id],
        hasAttemptedWithoutSelection: false,
      };
    }
    case "selectAll":
      return {
        ...state,
        selectedIds: action.allIds,
        hasAttemptedWithoutSelection: false,
      };
    case "toggleExpand":
      return {
        ...state,
        expandedId: state.expandedId === action.id ? null : action.id,
      };
    case "generateWithoutSelection":
      return {
        ...state,
        hasAttemptedWithoutSelection: true,
        selectionAttemptCount: state.selectionAttemptCount + 1,
      };
    case "generateStart":
      return { ...state, isGenerating: true, error: null };
    case "generateSuccess":
      return { ...state, isGenerating: false, draftOutput: action.output };
    case "generateError":
      return { ...state, isGenerating: false, error: action.error };
    case "adjustStart":
      return { ...state, isAdjusting: true, adjustError: null };
    case "adjustSuccess":
      return { ...state, isAdjusting: false, draftOutput: action.output };
    case "adjustError":
      return { ...state, isAdjusting: false, adjustError: action.error };
    case "resetToSelection":
      return { ...state, draftOutput: null, error: null, adjustError: null };
    default:
      return state;
  }
};

// ── Main component ─────────────────────────────────────────────────────────────

function DraftConversationPanel({
  messagePairs,
  onBackToConversation,
  onResultStateChange,
  resetSignal,
}: DraftConversationPanelProps) {
  const intl = useIntl();
  const { isAiAssistantAvailable, isChatQuotaExhausted } =
    useContext<AppContextInterface>(AppContext);
  const isAiEnabled = isAiAssistantAvailable;
  const [state, dispatch] = useReducer(
    reducer,
    messagePairs,
    createInitialState,
  );
  const {
    tone,
    length,
    selectedIds,
    isGenerating,
    error,
    draftOutput,
    expandedId,
    isAdjusting,
    adjustError,
    hasAttemptedWithoutSelection,
    selectionAttemptCount,
  } = state;

  useEffect(() => {
    onResultStateChange?.(!!draftOutput);
  }, [draftOutput, onResultStateChange]);

  useEffect(() => {
    if (resetSignal !== undefined) dispatch({ type: "resetToSelection" });
  }, [resetSignal]);

  const panelRef = useRef<HTMLDivElement>(null);
  const selectionErrorRef = useRef<HTMLParagraphElement>(null);

  useEffect(() => {
    if (selectionAttemptCount === 0) return;
    panelRef.current?.scrollTo({ top: 0 });
    selectionErrorRef.current?.focus({ preventScroll: true });
  }, [selectionAttemptCount]);

  const hasNoInsights = messagePairs.length === 0;
  const hasNoSelection = !hasNoInsights && selectedIds.length === 0;
  const showSelectionError = hasNoSelection && hasAttemptedWithoutSelection;

  const handleGenerate = async () => {
    if (!isAiEnabled || isChatQuotaExhausted) return;
    if (hasNoSelection) {
      dispatch({ type: "generateWithoutSelection" });
      return;
    }

    const selectedMessages = messagePairs
      .filter((p) => selectedIds.includes(p.id))
      .map((p) => p.response);

    dispatch({ type: "generateStart" });
    try {
      const output = await generateDraftMessage({
        inputMessage: selectedMessages,
        tone,
        length,
      });
      dispatch({ type: "generateSuccess", output });
    } catch {
      dispatch({
        type: "generateError",
        error: intl.formatMessage({ id: "app.toolboxChat.draftConversation.error" }),
      });
    }
  };

  const handleAdjust = async (adjustment: string): Promise<boolean> => {
    if (!draftOutput || !isAiEnabled || isChatQuotaExhausted) return false;

    dispatch({ type: "adjustStart" });
    try {
      const output = await generateDraftMessage({
        currentOutput: draftOutput,
        userPrompt: adjustment,
      });
      dispatch({ type: "adjustSuccess", output });
      return true;
    } catch {
      dispatch({
        type: "adjustError",
        error: intl.formatMessage({ id: "app.toolboxChat.draftResponse.adjust.error" }),
      });
      return false;
    }
  };

  if (isGenerating) {
    return (
      <div className="dcp dcp--loading">
        <img
          src="https://cdn.watermarkinsights.com/images/loaders/ai-pulsing-dot-loader.svg"
          alt={intl.formatMessage({ id: "app.toolboxChat.draftConversation.loading" })}
          className="draft-loader-img"
        />
        <span className="draft-loader-text">
          {intl.formatMessage({ id: "app.toolboxChat.draftConversation.loading" })}
        </span>
      </div>
    );
  }

  if (draftOutput) {
    return (
      <>
        <div className="dcp dcp--result">
          {isAdjusting ? (
            <div className="dcp__adjust-loading">
              <img
                src="https://cdn.watermarkinsights.com/images/loaders/ai-pulsing-dot-loader.svg"
                alt={intl.formatMessage({ id: "app.toolboxChat.draftConversation.adjustLoading" })}
                className="draft-loader-img"
              />
              <span className="draft-loader-text">
                {intl.formatMessage({ id: "app.toolboxChat.draftConversation.adjustLoading" })}
              </span>
            </div>
          ) : (
            <DraftResponseView output={draftOutput} />
          )}
          {isAiEnabled && isChatQuotaExhausted && (
            <QuotaLimitBanner
              className="dcp__quota-banner"
              title={intl.formatMessage({ id: "app.toolboxChat.quota.institutionExceeded.title" })}
              body={intl.formatMessage({ id: "app.toolboxChat.quota.institutionExceeded.body" })}
            />
          )}
        </div>
        {!isChatQuotaExhausted && (
          <DraftAdjustFooter
            onAdjust={handleAdjust}
            isAdjusting={isAdjusting}
            adjustError={adjustError}
          />
        )}
        <div className="dcp__footer">
          <WmButton
            buttonType="secondary"
            onClick={() => dispatch({ type: "resetToSelection" })}
          >
            {intl.formatMessage({
              id: "app.toolboxChat.draftConversation.action.backToDraftMessage",
            })}
          </WmButton>
          <WmButton buttonType="primary" onClick={onBackToConversation}>
            {intl.formatMessage({
              id: "app.toolboxChat.draftConversation.action.returnToConversation",
            })}
          </WmButton>
          {isAiEnabled && isChatQuotaExhausted && (
            <p className="dcp__quota-info">
              {intl.formatMessage({ id: "app.toolboxChat.draftConversation.quotaInfo" })}
            </p>
          )}
        </div>
      </>
    );
  }

  if (hasNoInsights) {
    return (
      <div className="dcp">
        <div className="dcp__empty">
          <svg width="32" height="32" viewBox="0 0 32 32" fill="none" aria-hidden="true" className="dcp__empty-icon">
            <path d="M10.4993 16.5424V18.834H12.7909L19.5498 12.0752L17.2582 9.78349L10.4993 16.5424ZM21.322 10.3029C21.5604 10.0646 21.5604 9.6796 21.322 9.44127L19.892 8.01127C19.6537 7.77294 19.2687 7.77294 19.0304 8.01127L17.912 9.1296L20.2037 11.4213L21.322 10.3029Z" fill="currentColor"/>
            <path d="M26.6667 2.66675H5.33341C3.86675 2.66675 2.66675 3.86675 2.66675 5.33341V29.3334L8.00008 24.0001H26.6667C28.1334 24.0001 29.3334 22.8001 29.3334 21.3334V5.33341C29.3334 3.86675 28.1334 2.66675 26.6667 2.66675ZM26.6667 21.3334H8.00008L5.33341 24.0001V5.33341H26.6667V21.3334Z" fill="currentColor"/>
          </svg>
          <p className="dcp__empty-text">
            {intl.formatMessage({ id: "app.toolboxChat.draftConversation.noInsights" })}
          </p>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="dcp" ref={panelRef}>
        <p className="draft-required-text">
          {intl.formatMessage({ id: "app.toolboxChat.draftConversation.requiredTextPart1" })}
          <span className="draft-required-asterisk"> * </span>
          {intl.formatMessage({ id: "app.toolboxChat.draftConversation.requiredTextPart2" })}
        </p>

        <p className="draft-intro">
          {intl.formatMessage({ id: "app.toolboxChat.draftConversation.intro" })}
        </p>

        <section className="draft-section">
          <p className="draft-section-label" id="dcp-tone-label">
            {intl.formatMessage({ id: "app.toolboxChat.draftMessage.toneLabel" })}
            <span className="draft-required-asterisk">*</span>
          </p>
          <PillGroup
            options={TONE_OPTIONS.map(({ value, messageId }) => ({
              value,
              label: intl.formatMessage({ id: messageId }),
            }))}
            selected={tone}
            name="dcp-tone"
            labelId="dcp-tone-label"
            onSelect={(value) => dispatch({ type: "setTone", tone: value })}
          />
        </section>

        <section className="draft-section">
          <p className="draft-section-label" id="dcp-length-label">
            {intl.formatMessage({ id: "app.toolboxChat.draftMessage.lengthLabel" })}
            <span className="draft-required-asterisk">*</span>
          </p>
          <PillGroup
            options={LENGTH_OPTIONS.map(({ value, messageId }) => ({
              value,
              label: intl.formatMessage({ id: messageId }),
            }))}
            selected={length}
            name="dcp-length"
            labelId="dcp-length-label"
            onSelect={(value) => dispatch({ type: "setLength", length: value })}
          />
        </section>

        <section className="draft-section">
          <div className="dcp__source-header">
            <p
              className={`dcp__source-label${showSelectionError ? " dcp__source-label--error" : ""}`}
              id="dcp-source-label"
            >
              {intl.formatMessage({ id: "app.toolboxChat.draftConversation.sourceLabel" })}
              <span className="dcp__source-count">
                {intl.formatMessage(
                  { id: "app.toolboxChat.draftConversation.sourceLabelCount" },
                  { count: selectedIds.length },
                )}
              </span>
              <span className="draft-required-asterisk">*</span>
              {showSelectionError && (
                <span className="dcp__source-warning" aria-hidden="true">⚠</span>
              )}
            </p>
            {messagePairs.length > 1 && <div className="dcp__select-all">
              <input
                type="checkbox"
                id="dcp-select-all"
                className="chkbx-input"
                checked={selectedIds.length === messagePairs.length}
                onChange={() => {
                  const allIds = selectedIds.length === messagePairs.length
                    ? []
                    : messagePairs.map((p) => p.id);
                  dispatch({ type: "selectAll", allIds });
                }}
              />
              <label htmlFor="dcp-select-all" className="chkbx">
                {intl.formatMessage({ id: "app.toolboxChat.draftConversation.selectAll" })}
              </label>
            </div>}
          </div>
          {showSelectionError && (
            <p
              ref={selectionErrorRef}
              tabIndex={-1}
              className="dcp__no-selection-msg"
              role="alert"
            >
              {intl.formatMessage({ id: "app.toolboxChat.draftConversation.noSelection" })}
            </p>
          )}

          <ul className="dcp__card-list" aria-labelledby="dcp-source-label">
            {messagePairs.map((pair) => {
              const isSelected = selectedIds.includes(pair.id);
              const isExpanded = expandedId === pair.id;
              return (
                <li key={pair.id} className="dcp__card-row">
                  {messagePairs.length > 1 && (
                    <>
                    <input
                      type="checkbox"
                      id={`dcp-select-${pair.id}`}
                      className="chkbx-input"
                      checked={isSelected}
                      onChange={() => dispatch({ type: "toggleId", id: pair.id })}
                      aria-label={pair.question || pair.response.slice(0, 60)}
                    />
                    <label htmlFor={`dcp-select-${pair.id}`} className="chkbx" />
                    </>
                  )}
                  <div className={`dcp__card${isSelected ? " dcp__card--selected" : ""}`}>
                    <div className="dcp__card-body">
                      {pair.question && (
                        <p className="dcp__card-title">{pair.question}</p>
                      )}
                      <DraftCardPreview
                        response={pair.response}
                        isExpanded={isExpanded}
                        onToggleExpand={() => dispatch({ type: "toggleExpand", id: pair.id })}
                      />
                    </div>
                  </div>
                </li>
              );
            })}
          </ul>
        </section>

        {error && <p className="draft-error">{error}</p>}
      </div>

      <div className="dcp__footer">
        <WmButton buttonType="secondary" onClick={onBackToConversation}>
          {intl.formatMessage({
            id: "app.toolboxChat.draftConversation.action.backToConversation",
          })}
        </WmButton>
        {isAiEnabled && (
          <WmButton
            icon="#ai-sparkles"
            buttonType="primary-ai"
            onClick={handleGenerate}
            disabled={isChatQuotaExhausted}
          >
            {intl.formatMessage({ id: "app.toolboxChat.draftMessage.action.generate" })}
          </WmButton>
        )}
        {isAiEnabled && isChatQuotaExhausted && (
          <p className="dcp__quota-info">
            {intl.formatMessage({ id: "app.toolboxChat.draftConversation.quotaInfo" })}
          </p>
        )}
      </div>
    </>
  );
}

export default DraftConversationPanel;
